import { StyleSheet } from 'react-native-unistyles';

/**
 * Shared Service Flow v2 visual language (spec §5.5, §8, §12.2).
 *
 * Restrained surfaces, eyebrow labels, a continuous time rail + cue markers
 * with the time label inline under the title (no dedicated gutter), and
 * angular cues that match CutCard / auth / Dashboard. Module colors only:
 * `colors.events` (flow/celebration cues), `colors.tasks` (task rows). No
 * invented colors.
 */

export const FLOW_MARKER_SIZE = 12;
export const FLOW_ROW_INSET = 12;
export const FLOW_ROW_GAP = 8;
// Rail line x-position: row inset + half the marker, minus half the 2px rail.
export const FLOW_RAIL_LEFT = FLOW_ROW_INSET + FLOW_MARKER_SIZE / 2 - 1;

export const serviceFlowStyles = StyleSheet.create(({ colors, fonts }) => ({
    // ---- Page / section chrome ----
    pageContainer: {
        flex: 1,
    },
    workspace: {
        width: '100%',
        maxWidth: 1180,
        alignSelf: 'center',
        padding: 20,
        gap: 16,
    },
    // Instance-tab content column — constrains content, NOT the ScrollView, so
    // the scrollbar sits at the screen edge. Horizontal padding stays on the
    // header/rows inside.
    flowContentColumn: {
        width: '100%',
        maxWidth: 1200,
        alignSelf: 'center',
    },
    eyebrow: {
        fontFamily: fonts.bold,
        fontSize: 11,
        letterSpacing: 1.6,
        textTransform: 'uppercase',
        color: colors.events,
    },
    title: {
        fontFamily: fonts.bold,
        fontSize: 24,
        lineHeight: 30,
        letterSpacing: -0.5,
        color: colors.text,
    },
    subtitle: {
        fontFamily: fonts.body,
        fontSize: 14,
        color: colors.onSurfaceVariant,
        lineHeight: 20,
    },
    headerRow: {
        flexDirection: 'row',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: 12,
        flexWrap: 'wrap',
    },
    headerActions: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
        flexWrap: 'wrap',
    },
    statusChip: (accent = false) => ({
        alignSelf: 'flex-start',
        paddingHorizontal: 10,
        paddingVertical: 4,
        backgroundColor: accent ? colors.eventsContainer : colors.surfaceElevated,
        borderWidth: 1,
        borderColor: accent ? colors.events : colors.borderSubtle,
    }),
    statusChipText: (accent = false) => ({
        fontFamily: fonts.bold,
        fontSize: 11,
        letterSpacing: 0.6,
        textTransform: 'uppercase',
        color: accent ? colors.events : colors.onSurfaceVariant,
    }),

    // ---- Timeline (rail + marker; time sits inline under the title) ----
    timeline: {
        position: 'relative',
    },
    rail: {
        position: 'absolute',
        // Starts under the first marker — never runs above it. The marker is
        // opaque, so taller first-row titles keep it hidden behind the cue.
        top: 24,
        bottom: 0,
        width: 2,
        backgroundColor: colors.borderSubtle,
    },
    row: {
        flexDirection: 'row',
        alignItems: 'stretch',
        paddingLeft: FLOW_ROW_INSET,
    },
    timeInline: {
        fontFamily: fonts.bold,
        fontSize: 12,
        color: colors.text,
        fontVariant: ['tabular-nums'],
    },
    // Marker renders inside rowHeader so alignItems centers it on the title.
    // Uniform eventsContainer cue for every type — the Chip carries type
    // identity.
    cueMarker: {
        width: FLOW_MARKER_SIZE,
        height: FLOW_MARKER_SIZE,
        borderRadius: FLOW_MARKER_SIZE / 2,
        backgroundColor: colors.eventsContainer,
    },
    rowContent: {
        flex: 1,
        paddingTop: 8,
        paddingBottom: 14,
        paddingRight: 16,
        gap: 4,
    },
    rowHeader: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: FLOW_ROW_GAP,
    },
    // Details card (meta/notes/embedded task content) indents under the title.
    detailsCard: {
        marginLeft: FLOW_MARKER_SIZE + FLOW_ROW_GAP,
    },
    detailsCardContent: {
        paddingHorizontal: 12,
        paddingVertical: 10,
        gap: 6,
    },
    rowTitle: (muted = false) => ({
        flex: 1,
        fontFamily: fonts.bold,
        fontSize: 15,
        color: muted ? colors.onSurfaceVariant : colors.text,
    }),
    rowMeta: {
        fontFamily: fonts.body,
        fontSize: 12,
        color: colors.onSurfaceVariant,
    },
    rowMetaWarn: {
        fontFamily: fonts.body,
        fontSize: 12,
        color: colors.tasks,
    },
    notesToggleText: {
        fontFamily: fonts.bold,
        fontSize: 12,
        color: colors.events,
    },
    fieldLabel: {
        fontFamily: fonts.heading,
        fontSize: 13,
        color: colors.textSecondary,
        paddingBottom: 6,
    },
    notesHeading: {
        fontFamily: fonts.bold,
        fontSize: 10,
        color: colors.onSurfaceVariant,
        letterSpacing: 0.8,
        textTransform: 'uppercase',
        marginBottom: 2,
    },
    notesBody: {
        fontFamily: fonts.body,
        fontSize: 13,
        color: colors.onSurfaceVariant,
        lineHeight: 19,
    },

    rowMenuButton: {
        padding: 12,
        minWidth: 44,
        minHeight: 44,
        alignItems: 'center',
        justifyContent: 'center',
    },

    // ---- Task context / placeholders ----
    // Restricted notices render inside the row's details card — no box chrome.
    restrictedTask: {
        paddingVertical: 2,
    },
    restrictedTaskText: {
        fontFamily: fonts.body,
        fontSize: 13,
        color: colors.onSurfaceVariant,
    },
    worshipPlaceholder: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
        padding: 10,
        borderWidth: 1,
        borderColor: colors.borderSubtle,
        backgroundColor: colors.eventsContainer,
    },
    worshipPlaceholderText: {
        fontFamily: fonts.body,
        fontSize: 13,
        color: colors.onSurfaceVariant,
        flex: 1,
    },

    // ---- Type picker ----
    typeOptions: {
        gap: 10,
    },
    // Rendered as a CutCard contentStyle — card supplies the surface.
    typeOption: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 12,
        padding: 14,
    },
    typeOptionIcon: {
        width: 36,
        height: 36,
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: colors.eventsContainer,
    },
    typeOptionTitle: {
        fontFamily: fonts.bold,
        fontSize: 15,
        color: colors.text,
    },
    typeOptionDescription: {
        fontFamily: fonts.body,
        fontSize: 12,
        color: colors.onSurfaceVariant,
        marginTop: 2,
    },

    // ---- Item editor ----
    editorContainer: {
        gap: 20,
        // The angular inputs draw their focus ring 2px outside the control;
        // insets inside the sheet's scroll container keep it from clipping.
        padding: 4,
    },
    editorSection: {
        gap: 6,
    },
    editorFieldLabel: {
        fontFamily: fonts.bold,
        fontSize: 11,
        letterSpacing: 1.2,
        textTransform: 'uppercase',
        color: colors.onSurfaceVariant,
        marginBottom: 6,
    },
    editorError: {
        fontFamily: fonts.body,
        fontSize: 12,
        color: colors.error,
        marginTop: 4,
    },
    // Side-panel editor header — eyebrow title left, Save/Cancel right.
    editorPanelHeader: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 8,
    },
    editorPanelActions: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
    },
    helperText: {
        fontFamily: fonts.body,
        fontSize: 12,
        color: colors.onSurfaceVariant,
        lineHeight: 17,
    },
    calloutAction: {
        alignSelf: 'flex-start',
        marginTop: 4,
    },
    calloutActionText: {
        fontFamily: fonts.bold,
        fontSize: 12,
        color: colors.events,
    },

    // ---- Library cards / list ----
    cardTitle: {
        fontFamily: fonts.bold,
        fontSize: 17,
        color: colors.text,
    },
    cardDescription: {
        fontFamily: fonts.body,
        fontSize: 13,
        color: colors.onSurfaceVariant,
        marginTop: 4,
        lineHeight: 18,
    },
    cardMetaRow: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 12,
        marginTop: 10,
    },
    cardMeta: {
        fontFamily: fonts.body,
        fontSize: 12,
        color: colors.onSurfaceVariant,
    },
    cardActions: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
        marginTop: 12,
    },
    emptyState: {
        alignItems: 'center',
        paddingVertical: 48,
        paddingHorizontal: 24,
        gap: 10,
    },
    emptyTitle: {
        fontFamily: fonts.bold,
        fontSize: 16,
        color: colors.text,
        textAlign: 'center',
    },
    emptyBody: {
        fontFamily: fonts.body,
        fontSize: 13,
        color: colors.onSurfaceVariant,
        textAlign: 'center',
        lineHeight: 19,
    },

    // ---- Selector ----
    selectorValue: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 10,
        padding: 12,
        borderWidth: 1,
        borderColor: colors.borderSubtle,
        backgroundColor: colors.surface,
    },
    selectorValueText: {
        flex: 1,
        fontFamily: fonts.bold,
        fontSize: 14,
        color: colors.text,
    },
    selectorPreview: {
        borderWidth: 1,
        borderColor: colors.borderSubtle,
        backgroundColor: colors.surfaceElevated,
        padding: 12,
        gap: 8,
        marginTop: 8,
    },
    selectorPreviewRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 10,
    },
    selectorPreviewTime: {
        minWidth: 84,
        flexShrink: 0,
        fontFamily: fonts.bold,
        fontSize: 11,
        color: colors.onSurfaceVariant,
        fontVariant: ['tabular-nums'],
    },
    selectorPreviewTitle: {
        flex: 1,
        fontFamily: fonts.body,
        fontSize: 13,
        color: colors.text,
    },

    // ---- Split workspace ----
    splitRow: {
        flexDirection: 'row',
        gap: 20,
        alignItems: 'flex-start',
    },
    splitTimeline: {
        flex: 1,
        minWidth: 0,
    },
    splitPanel: {
        width: 360,
        flexShrink: 0,
    },
}));
